import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/context/AuthContext";
import { ConductorSizingCard, ArcFlashCard } from "@/components/Calculators";
import BatchConductor from "@/components/BatchConductor";
import BusBarSizingCard from "@/components/BusBarSizingCard";
import SchematicGeneratorPanel from "@/components/SchematicGeneratorPanel";

/**
 * ToolsPage — v2.44 — Renamed from "Calculators" to "Tools". Now hosts
 * conductor-sizing, arc-flash IEEE-1584, and the new Schematic Generator
 * (CSV-driven mass drawing generator).
 * v2.79 — supports deep-link `?tab=batch&batch=<id>` from the project's
 * Sizing tab so saved batches open instantly with rows + results restored.
 * v2.82 — added Bus bar sizing tab (CDA #22 / IEC 61439).
 * v3.2.19 — Tools + Bus bar sizing are admin-only (route + intra-page gate).
 */
export default function ToolsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "batch" ? "batch" :
                     params.get("tab") === "conductor" ? "conductor" :
                     params.get("tab") === "arcflash" ? "arcflash" :
                     params.get("tab") === "busbar" ? (isAdmin ? "busbar" : "schematic") :
                     params.get("tab") === "schematic" ? "schematic" :
                     "schematic";
  const autoLoadBatchId = params.get("batch") || "";
  const [tab, setTab] = useState(initialTab);

  const TABS = [
    { id: "schematic", label: "Schematic Generator", badge: "NEW" },
    { id: "conductor", label: "Conductor sizing" },
    { id: "batch",     label: "Batch conductor" },
    // v3.2.19 · Bus bar sizing is admin-only.
    ...(isAdmin ? [{ id: "busbar", label: "Bus bar sizing", badge: "NEW" }] : []),
    { id: "arcflash",  label: "Arc-flash · IEEE 1584" },
  ];

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="tools" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Tools</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="tools-page-heading">
          Tools
        </h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Quick-reference calculators + drawing-generation utilities. Every output must be reviewed and stamped by a licensed Professional Engineer.
        </p>

        <div className="mt-6 inline-flex border border-white/10 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-mono inline-flex items-center gap-2 ${tab === t.id ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-white"}`}
              data-testid={`tools-tab-${t.id}`}
            >
              {t.label}
              {t.badge && (
                <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${tab === t.id ? "bg-black text-[#00E5FF]" : "bg-[#00E5FF] text-black"}`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "schematic" && <SchematicGeneratorPanel />}
          {tab === "conductor" && <ConductorSizingCard />}
          {tab === "batch"     && <BatchConductor autoLoadBatchId={autoLoadBatchId} />}
          {tab === "busbar" && isAdmin && <BusBarSizingCard />}
          {tab === "arcflash"  && <ArcFlashCard />}
        </div>
      </div>
    </div>
  );
}
